import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { tk, setCheckoutLang } from "../lib/checkoutI18n";
setCheckoutLang(localStorage.getItem("lang") || "it");
const LOCALE = { it: "it-IT", en: "en-GB", es: "es-ES", fr: "fr-FR", de: "de-DE" }[localStorage.getItem("lang") || "it"] || "it-IT";
import {
  CheckCircle2, Loader2, XCircle, Home, Phone, Clock, ChefHat,
  PackageCheck, Bike, Sparkles, Recycle, ShoppingBag, Leaf,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;

// Ordered steps shown in the timeline. Delivery orders include the
// 'delivering' step; pickup orders skip it.
const STEPS_BASE = [
  { key: "new",        label: tk("orderReceived"), icon: CheckCircle2 },
  { key: "preparing",  label: tk("cooking"),      icon: ChefHat },
  { key: "ready",      label: tk("ready"),          icon: PackageCheck },
  { key: "delivering", label: tk("delivering"),     icon: Bike, deliveryOnly: true },
  { key: "completed",  label: tk("delivered"),      icon: Sparkles },
];

function timelineFor(mode) {
  return STEPS_BASE.filter((s) => !s.deliveryOnly || mode === "delivery");
}

function stateIndex(steps, status) {
  const i = steps.findIndex((s) => s.key === status);
  return i >= 0 ? i : 0;
}

/**
 * /asporto/success — landed here after Stripe redirect (?session_id=...)
 * or immediately after cash/paypal order (?order_id=...).
 * Polls the order every 20s to reflect state transitions triggered by
 * the admin panel.
 */
export default function TakeawaySuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const cashOrderId = params.get("order_id");
  const [state, setState] = useState({ loading: true, order: null, paid: false });

  useEffect(() => {
    let cancelled = false;
    let stripeAttempts = 0;

    async function loadOnce(id) {
      try {
        const { data } = await axios.get(`${API}/takeaway/orders/${id}`);
        if (!cancelled) setState((s) => ({ ...s, loading: false, order: data, paid: data.payment_status === "paid" }));
      } catch { /* transient */ }
    }

    async function pollStripe() {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/takeaway/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await loadOnce(data.order_id);
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ loading: false, order: null, paid: false, failed: true });
          return;
        }
      } catch { /* transient */ }
      stripeAttempts++;
      if (stripeAttempts > 30) {
        setState({ loading: false, order: null, paid: false, timeout: true });
        return;
      }
      setTimeout(pollStripe, 2000);
    }

    if (sessionId) pollStripe();
    else if (cashOrderId) loadOnce(cashOrderId);
    else setState({ loading: false, order: null, paid: false });

    // Order-state polling every 20s once we have the order id.
    const pollState = setInterval(() => {
      const id = state.order?.id || cashOrderId;
      if (id && !cancelled) loadOnce(id);
    }, 20000);

    return () => { cancelled = true; clearInterval(pollState); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, cashOrderId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#faf3ee] flex flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-[#4a1c17]" />
        <p className="text-sm text-[#7c5a4e]">Confermiamo il pagamento…</p>
      </div>
    );
  }

  if (state.failed || state.timeout || !state.order) {
    return (
      <div className="min-h-screen bg-[#faf3ee] flex flex-col items-center justify-center gap-3 px-6">
        <XCircle className="h-14 w-14 text-red-500" />
        <h1 className="font-serif text-2xl font-bold text-[#2b1410] text-center">{tk("orderNotFound")}</h1>
        <p className="text-sm text-[#7c5a4e] text-center">{tk("contactUs")} +39 351 8120603</p>
        <button onClick={() => nav("/asporto")} className="rounded-full bg-[#4a1c17] text-[#f7ebdf] px-4 py-2 text-sm">{tk("backToMenu")}</button>
      </div>
    );
  }

  const o = state.order;
  const steps = timelineFor(o.mode);
  const idx = stateIndex(steps, o.status || "new");

  return (
    <div className="min-h-screen bg-[#faf3ee] py-10 px-4">
      <div className="max-w-lg mx-auto rounded-2xl bg-white border border-[#e8dcc8] p-6 shadow-sm">
        <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-600" />
        <h1 className="mt-3 text-center font-serif text-2xl font-bold text-[#2b1410]">
          {tk("orderConfirmed")}
        </h1>
        <p className="mt-1 text-center text-sm text-[#7c5a4e]">
          {tk("thanks")} <strong>{o.name}</strong>! {tk("seeYouAt")} <strong>{o.slot}</strong>
        </p>

        {/* Tracking timeline */}
        <Timeline steps={steps} idx={idx} history={o.state_history || []} />

        <div className="mt-5 rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-4 space-y-1 text-sm">
          <Row label={tk("orderCode")} value={`#${o.id.slice(0, 8)}`} />
          <Row label={tk("date")} value={new Date(o.date).toLocaleDateString(LOCALE, { weekday: "long", day: "numeric", month: "long" })} />
          <Row label={tk("mode")} value={o.mode === "delivery" ? tk("homeDelivery") : tk("pickupOnSite")} />
          {o.mode === "delivery" && <Row label={tk("address")} value={o.address} />}
          <Row label={tk("payment")} value={o.payment_status === "paid" ? tk("paidOnline") : tk("atPickup")} />
        </div>

        <div className="mt-4 rounded-xl bg-white border border-[#e8dcc8] p-4">
          <h3 className="font-serif font-bold text-[#2b1410] mb-2">{tk("summary")}</h3>
          <ul className="text-sm space-y-1">
            {o.items.map((it, i) => (
              <li key={i} className="flex justify-between">
                <span>{it.qty}× {it.name}</span>
                <span className="text-[#7c5a4e]">{eur(it.line_total)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-[#e8dcc8] text-sm space-y-0.5">
            <Row label={tk("subtotal")} value={eur(o.subtotal)} />
            {o.delivery_fee > 0 && <Row label={tk("delivery")} value={eur(o.delivery_fee)} />}
            {o.packaging?.extra_cost > 0 && <Row label={tk("packaging")} value={eur(o.packaging.extra_cost)} />}
            {o.packaging?.credit > 0 && <Row label={tk("returnedDiscount")} value={`- ${eur(o.packaging.credit)}`} />}
            <Row label={tk("total")} value={eur(o.total)} bold />
          </div>
        </div>

        {/* Packaging choice — shown only if the customer hasn't picked yet */}
        {!o.packaging && (
          <PackagingCard order={o} onPicked={(updated) => setState((s) => ({ ...s, order: updated }))} />
        )}
        {o.packaging && <PackagingSummary p={o.packaging} mode={o.mode} />}

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <a href="tel:+393518120603" className="flex-1 rounded-full border border-[#e8dcc8] text-[#4a1c17] px-4 py-2 text-sm text-center inline-flex items-center justify-center gap-1">
            <Phone className="h-3.5 w-3.5" /> Chiama la struttura
          </a>
          <button onClick={() => nav("/asporto")} className="flex-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm inline-flex items-center justify-center gap-1">
            <Home className="h-3.5 w-3.5" /> Nuovo ordine
          </button>
        </div>
      </div>
    </div>
  );
}

function Timeline({ steps, idx, history }) {
  const atByState = Object.fromEntries((history || []).map((h) => [h.state, h.at]));
  const fmtTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };
  return (
    <ol className="mt-6 space-y-3" data-testid="takeaway-timeline">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        const Icon = s.icon;
        return (
          <li key={s.key} className="flex items-center gap-3" data-testid={`timeline-step-${s.key}`}>
            <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
              done ? "bg-[#4a1c17] text-[#f7ebdf]" :
              active ? "bg-[#c48b31] text-[#2b1410] ring-2 ring-[#c48b31]/40 animate-pulse" :
              "bg-[#e8dcc8] text-[#7c5a4e]"
            }`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className={`font-serif text-sm ${done || active ? "font-bold text-[#2b1410]" : "text-[#7c5a4e]"}`}>
                {s.label}
              </div>
              {atByState[s.key] && (
                <div className="text-[11px] text-[#7c5a4e]">
                  <Clock className="inline h-3 w-3 mr-1" />{fmtTime(atByState[s.key])}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-[#2b1410] pt-1 border-t border-[#e8dcc8]" : "text-[#7c5a4e]"}`}>
      <span>{label}</span><span className="text-right">{value}</span>
    </div>
  );
}

/**
 * PackagingCard — post-order upsell offering the 3 reusable-packaging
 * choices. Only rendered if the customer hasn't chosen yet. Shows the
 * "return_now" option only for delivery orders (pickup guests already
 * leave with the packaging).
 */
function PackagingCard({ order, onPicked }) {
  const [cfg, setCfg] = useState(null);
  const [choice, setChoice] = useState(null);   // 'return_now' | 'keep_reuse' | 'purchase'
  const [qtyByType, setQtyByType] = useState({}); // { key: qty }
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    axios.get(`${API}/takeaway/packaging-config`).then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  if (!cfg) return null;
  const purchaseTotal = (cfg.types || []).reduce(
    (s, t) => s + (Number(qtyByType[t.key] || 0) * Number(t.purchase_price)), 0,
  );
  const canPurchase = choice === "purchase" && purchaseTotal > 0;
  const canSubmit = choice === "return_now" || choice === "keep_reuse" || canPurchase;
  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body = { choice };
      if (choice === "purchase") {
        body.purchase_items = Object.entries(qtyByType)
          .filter(([, qty]) => Number(qty) > 0)
          .map(([type, qty]) => ({ type, qty: Number(qty) }));
      }
      const { data } = await axios.patch(`${API}/takeaway/orders/${order.id}/packaging`, body);
      onPicked(data);
    } catch { /* keep card open on error */ }
    finally { setSubmitting(false); }
  };
  return (
    <div className="mt-4 rounded-xl bg-[#faf3ee] border border-[#c48b31]/40 p-4" data-testid="packaging-card">
      <div className="flex items-center gap-2 mb-1">
        <Recycle className="h-4 w-4 text-[#4a1c17]" />
        <h3 className="font-serif font-bold text-[#2b1410]">Le tue confezioni riusabili</h3>
      </div>
      <p className="text-xs text-[#7c5a4e] mb-3">
        Usiamo contenitori riusabili per ridurre plastica e sprechi. Scegli come gestire i tuoi:
      </p>
      <div className="space-y-2">
        {order.mode === "delivery" && (
          <PackagingOption
            active={choice === "return_now"}
            onClick={() => setChoice("return_now")}
            testId="pkg-return-now"
            icon={Bike}
            title="Rimanda subito col fattorino"
            hint={`Il fattorino attende e le riporta indietro · sconto immediato di € ${Number(cfg.return_credit).toFixed(2)} sul totale`}
          />
        )}
        <PackagingOption
          active={choice === "keep_reuse"}
          onClick={() => setChoice("keep_reuse")}
          testId="pkg-keep"
          icon={Leaf}
          title="Le tengo e le restituisco al prossimo ordine"
          hint={`Gratis · se non ci sono nuovi ordini entro ${cfg.keep_reuse_days} giorni ci accordiamo per ritirarle`}
        />
        <PackagingOption
          active={choice === "purchase"}
          onClick={() => setChoice("purchase")}
          testId="pkg-purchase"
          icon={ShoppingBag}
          title="Le acquisto (le tengo per sempre)"
          hint="Robuste, lavabili, utili in cucina o per il picnic"
        />
        {choice === "purchase" && (
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pkg-purchase-items">
            {(cfg.types || []).map((t) => (
              <div key={t.key} className="rounded-lg bg-white border border-[#e8dcc8] p-3 flex items-center gap-3">
                <span className="text-2xl">{t.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#2b1410]">{t.label_it}</div>
                  <div className="text-[11px] text-[#7c5a4e]">€ {Number(t.purchase_price).toFixed(2)} cad.</div>
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setQtyByType((q) => ({ ...q, [t.key]: Math.max(0, (Number(q[t.key]) || 0) - 1) }))}
                    className="h-7 w-7 rounded-full bg-[#faf3ee] border border-[#e8dcc8] text-[#4a1c17]">−</button>
                  <input readOnly value={qtyByType[t.key] || 0} className="w-8 text-center font-semibold text-sm bg-transparent" data-testid={`pkg-qty-${t.key}`} />
                  <button type="button" onClick={() => setQtyByType((q) => ({ ...q, [t.key]: (Number(q[t.key]) || 0) + 1 }))}
                    className="h-7 w-7 rounded-full bg-[#4a1c17] text-[#f7ebdf]" data-testid={`pkg-plus-${t.key}`}>+</button>
                </div>
              </div>
            ))}
            {purchaseTotal > 0 && (
              <div className="sm:col-span-2 rounded-lg bg-[#c48b31]/15 border border-[#c48b31]/40 px-3 py-2 text-sm text-[#4a1c17] flex justify-between">
                <span>{tk("totalPackaging")}</span>
                <span className="font-bold">€ {purchaseTotal.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit || submitting}
        data-testid="pkg-submit"
        className="mt-4 w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
      >
        {submitting ? <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> : null}
        {choice === "purchase" && purchaseTotal > 0
          ? `${tk("addPackaging")} · € ${purchaseTotal.toFixed(2)}`
          : tk("confirmChoice")}
      </button>
    </div>
  );
}

function PackagingOption({ active, onClick, testId, icon: Icon, title, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left rounded-xl border p-3 transition-colors ${
        active
          ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]"
          : "bg-white text-[#2b1410] border-[#e8dcc8] hover:border-[#4a1c17]"
      }`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 ${active ? "text-[#e6c46a]" : "text-[#c48b31]"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{title}</div>
          <div className={`text-xs ${active ? "text-[#f7ebdf]/80" : "text-[#7c5a4e]"}`}>{hint}</div>
        </div>
      </div>
    </button>
  );
}

function PackagingSummary({ p, mode }) {
  const label = {
    return_now:   "Le confezioni rientreranno subito col fattorino",
    keep_reuse:   "Le confezioni resteranno con te fino al prossimo ordine",
    purchase:     "Confezioni acquistate",
  }[p.choice];
  return (
    <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" data-testid="packaging-summary">
      <div className="flex items-center gap-2 font-semibold"><Recycle className="h-4 w-4" /> {label}</div>
      {p.choice === "return_now" && p.credit > 0 && (
        <div className="text-xs mt-1">Sconto di € {Number(p.credit).toFixed(2)} già applicato al totale di quest'ordine.</div>
      )}
      {p.choice === "keep_reuse" && p.keep_until_days && (
        <div className="text-xs mt-1">
          Riportale al prossimo ordine. Se entro {p.keep_until_days} giorni non prenoti di nuovo, ci accordiamo per ritirarle.
        </div>
      )}
      {p.choice === "purchase" && p.items?.length > 0 && (
        <ul className="text-xs mt-1 space-y-0.5">
          {p.items.map((it, i) => (
            <li key={i} className="flex justify-between"><span>{it.qty}× {it.label}</span><span>€ {it.line_total.toFixed(2)}</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}
